import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import {
  Monitor, Bell, Server, Eye, FileText, Users,
  Shield, Zap, Globe, HardDrive, BarChart3, Lock,
  ArrowRight, CheckCircle
} from "lucide-react";

const mainFeatures = [
  {
    icon: Monitor,
    title: "Dashboard centralise",
    desc: "Visualisez l'etat de toutes vos sauvegardes Veeam et Iperius depuis une interface unique. Statuts en temps reel, historique detaille et filtrage par client, serveur ou statut.",
    details: ["Vue d'ensemble multi-clients", "Historique par serveur et par VM", "Filtres avances par statut", "Rafraichissement automatique"],
    color: "cyan",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: Bell,
    title: "Alertes et notifications",
    desc: "Configurez votre SMTP et recevez instantanement un email lorsqu'une sauvegarde echoue ou presente un avertissement. Resume quotidien configurable.",
    details: ["Alertes email en temps reel", "Resume quotidien automatique", "Configuration SMTP personnalisee", "Filtrage par severite"],
    color: "amber",
    image: "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: Server,
    title: "Agents deploiement facile",
    desc: "Un simple script PowerShell a executer sur vos serveurs Windows. L'agent remonte automatiquement les informations de sauvegarde. Telechargement en un clic.",
    details: ["Script PowerShell natif Windows", "Configuration automatique", "Agent leger sans installation", "Telechargement ZIP avec documentation"],
    color: "emerald",
    image: "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: Eye,
    title: "Monitoring public",
    desc: "Chaque client dispose d'un code unique pour consulter l'etat de ses sauvegardes sans compte. Ideal pour les ecrans de monitoring en affichage defilant.",
    details: ["Code d'acces unique par client", "Vue publique sans authentification", "Mode kiosque pour ecrans TV", "Auto-rafraichissement continu"],
    color: "indigo",
    image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: FileText,
    title: "Rapports et exports",
    desc: "Generez des rapports professionnels en PDF ou CSV en un clic. Par serveur ou par client, avec statistiques de taux de succes et historique detaille.",
    details: ["Export CSV et PDF", "Rapports par serveur ou client", "Statistiques de taux de succes", "Ideal pour les revues periodiques"],
    color: "rose",
    image: "https://images.unsplash.com/photo-1557200134-90327ee9fafa?auto=format&fit=crop&w=800&q=80",
  },
  {
    icon: Users,
    title: "Architecture multi-tenant",
    desc: "Chaque utilisateur dispose de son espace isole. Les donnees clients, serveurs et historiques sont completement cloisonnees entre les comptes.",
    details: ["Isolation totale des donnees", "Roles admin et utilisateur", "Gestion multi-clients", "Abonnements par utilisateur"],
    color: "purple",
    image: "https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?auto=format&fit=crop&w=800&q=80",
  },
];

const additionalFeatures = [
  { icon: Shield, title: "Securite", desc: "Authentification JWT, chiffrement des mots de passe, isolation multi-tenant." },
  { icon: Zap, title: "Rapide", desc: "API asynchrone FastAPI, reponses en millisecondes, agents legers." },
  { icon: Globe, title: "Accessible partout", desc: "Application web accessible depuis n'importe quel navigateur, sans VPN." },
  { icon: HardDrive, title: "Multi-logiciels", desc: "Support natif Veeam Backup et Iperius Backup, d'autres a venir." },
  { icon: BarChart3, title: "Statistiques", desc: "Taux de succes, tendances, metriques par client et par periode." },
  { icon: Lock, title: "RGPD", desc: "Donnees hebergees en Europe, suppression possible sur demande." },
];

const colorMap = {
  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", dot: "bg-cyan-500" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", dot: "bg-emerald-500" },
  indigo: { bg: "bg-indigo-500/10", border: "border-indigo-500/20", text: "text-indigo-400", dot: "bg-indigo-500" },
  rose: { bg: "bg-rose-500/10", border: "border-rose-500/20", text: "text-rose-400", dot: "bg-rose-500" },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", dot: "bg-purple-500" },
};

const Features = () => {
  return (
    <div className="min-h-screen bg-[#07090E]">
      <PublicNavbar />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-4">Fonctionnalites</p>
          <h1
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Une plateforme complete pour vos sauvegardes
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Monitoring en temps reel, alertes, rapports, agents deploiement facile et monitoring public client.
            Tout est integre dans ServeurOnline.
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-16" data-testid="main-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {mainFeatures.map((feat, i) => {
            const c = colorMap[feat.color];
            const isReversed = i % 2 !== 0;
            return (
              <div
                key={i}
                className={`grid lg:grid-cols-2 gap-12 items-center ${isReversed ? "lg:grid-flow-col-dense" : ""}`}
                data-testid={`feature-detail-${i}`}
              >
                <div className={isReversed ? "lg:col-start-2" : ""}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${c.bg} border ${c.border} mb-5`}>
                    <feat.icon className={c.text} size={14} />
                    <span className={`text-xs font-mono ${c.text}`}>{feat.title}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {feat.title}
                  </h2>
                  <p className="text-slate-400 mb-8 leading-relaxed">{feat.desc}</p>
                  <div className="space-y-3">
                    {feat.details.map((d, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <CheckCircle className={c.text} size={16} />
                        <span className="text-sm text-slate-300">{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`${isReversed ? "lg:col-start-1" : ""} rounded-2xl border ${c.border} overflow-hidden max-h-[200px]`}>
                  <img src={feat.image} alt={feat.title} className="w-full h-[200px] object-cover" loading="lazy" />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Additional features grid */}
      <section className="py-20 bg-[#0D111A]/30" data-testid="additional-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Et bien plus encore
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalFeatures.map((feat, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-800/60 bg-[#0D111A]/70 hover:border-slate-700 transition-colors">
                <feat.icon className="text-cyan-400 mb-3" size={22} />
                <h3 className="text-white font-semibold mb-1">{feat.title}</h3>
                <p className="text-sm text-slate-400">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Convaincu ?
          </h2>
          <p className="text-slate-400 mb-8">Testez ServeurOnline gratuitement pendant 14 jours. Aucune carte bancaire requise.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-xl" data-testid="features-cta-register">
                Commencer l'essai gratuit
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="ghost" className="text-slate-300 hover:text-white border border-slate-700 px-8 py-6 text-base rounded-xl" data-testid="features-cta-pricing">
                Voir les tarifs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;
